'use client';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Howl } from 'howler';
import type { KrpanoType, SceneData } from '@/types/krpano'; // Import the KrpanoType and SceneData interfaces
import { useIdPath } from './IdPathProvider';
import projectServices from '@/utils/services/project';
import { sceneServices } from '@/templates/Heritage/utils/services/scene';
import { waitForFirstScene, getCurrentScene } from '@/utils/krpanoUtils';

// Stable helper — defined once, never re-created
const krpanoSanitize = (s: string | number | boolean) =>
    String(s).replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

// Safe XSS whitelist and sanitizers for URL query parameters
const ALLOWED_TABS = new Set(['nav_gallery', 'nav_autotour', 'nav_material', 'nav_group_view']);
const ALLOWED_TYPES = new Set(['video', 'photo', 'image', 'document', 'album', 'model', '3d_model']);
const ALLOWED_LANGS = new Set(['vn', 'en']);
const SAFE_PARAM_REGEX = /^[a-zA-Z0-9_-]{1,64}$/;

function sanitizeSafeParam(val: string | null): string | undefined {
    if (!val) return undefined;
    const clean = val.trim();
    if (clean === 'undefined' || clean === 'null' || !clean) return undefined;
    if (SAFE_PARAM_REGEX.test(clean)) return clean;
    return undefined;
}

// Navigation types
export interface NavigationParams {
    tab?: 'nav_gallery' | 'nav_autotour' | 'nav_material' | 'nav_group_view';
    id?: string;
    type?: string;
    lang?: string;
    startscene?: string;
    category?: string;
    source?: string;
    single?: string;
}

interface KrpanoContextType {
    // projectValue: Record<any, any>; // Updated type for projectValue

    isReadyKrpano: boolean; // New state to track if Krpano is ready
    setIsReadyKrpano: React.Dispatch<React.SetStateAction<boolean>>;

    krpano: KrpanoType | null; // Updated type for krpano
    setKrpano: React.Dispatch<React.SetStateAction<KrpanoType | null>>; // Updated type for setKrpano
    krpanoCurrent: SceneData | null; // Updated type for krpanoCurrent to SceneData
    setKrpanoCurrent: React.Dispatch<React.SetStateAction<SceneData | null>>; // Updated type for setKrpanoCurrent to SceneData

    previousSceneRef: React.RefObject<string>; // Updated type for previousSceneRef to React.RefObject<string>

    currentScene: string; // Updated type for currentScene
    setCurrentScene: React.Dispatch<React.SetStateAction<string>>;

    sound: Howl | null;
    setSound: React.Dispatch<React.SetStateAction<Howl | null>>;
    userInteracted: boolean;
    setUserInteracted: React.Dispatch<React.SetStateAction<boolean>>;
    isTurnOnSound: boolean;
    setIsTurnOnSound: React.Dispatch<React.SetStateAction<boolean>>;

    // Navigation functions
    navigateToView: (params: NavigationParams) => void;
    navigateToGallery: (modelId: string, lang?: string) => void;
    navigateToAutoTour: (tourId: string, startScene?: string, lang?: string) => void;
    navigateToMaterial: (materialId: string, type: string, lang?: string) => void;
    parseCurrentUrl: () => NavigationParams;
    createNavigationUrl: (baseUrl: string, params: NavigationParams) => string;
}

interface TourXmlType {
    xml: string;
    swf: string;
}

interface Scene {
    scene_name: string;
    scene_group: string;
    name: string;
    thumbnail: string;
    isShow: string;
    STT: string;
    type: string;
    scene_default: {
        scene_name: string;
        hlookat: string;
        vlookat: string;
        fov: string;
        fovmin: string;
        fovmax: string;
        nSeen: string;
    };
    scene_lock: {
        scene_name: string;
        limitview: string;
        hlookatmin: string;
        hlookatmax: string;
        vlookatmin: string;
        vlookatmax: string;
    };
}

export const KrpanoContext = createContext<KrpanoContextType | undefined>(undefined);
export const KrpanoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { idPath, projectValue } = useIdPath();

    const [templateId, setTemplateId] = useState<string>('heritage'); // Updated type for templateId

    const [isReadyKrpano, setIsReadyKrpano] = useState<boolean>(false);

    const [krpano, setKrpano] = useState<KrpanoType | null>(null); // Updated type for krpano
    const [krpanoCurrent, setKrpanoCurrent] = useState<SceneData | null>(null); // Updated type for krpanoCurrent to SceneData

    const previousSceneRef = React.useRef<string>(''); // Using a ref to store previousScene
    // const [previousScene, setPreviousScene] = useState<string>(''); // Updated type for previousScene to string or null
    const [currentScene, setCurrentScene] = useState<string>(''); // Updated type for krpanoCurrent to SceneData
    // Ref so the polling interval can read currentScene without being in its dependency array
    const currentSceneRef = useRef<string>(currentScene);
    useEffect(() => { currentSceneRef.current = currentScene; }, [currentScene]);

    const [sound, setSound] = useState<Howl | null>(null);
    const [userInteracted, setUserInteracted] = useState<boolean>(false);
    const [isTurnOnSound, setIsTurnOnSound] = useState<boolean>(false);

    // Navigation functions
    const navigateToView = (params: NavigationParams) => {
        const { tab, id, type, lang, startscene, category } = params;

        // Tạo URL parameters
        const urlParams = new URLSearchParams();

        if (tab) urlParams.set('tab', tab);
        if (id) urlParams.set('id', id);
        if (type) urlParams.set('type', type);
        if (lang) urlParams.set('lang', lang);
        if (startscene) urlParams.set('startscene', startscene);
        if (category) urlParams.set('category', category);
        // Cập nhật URL không reload trang
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.pushState({}, '', newUrl);
    };

    const parseCurrentUrl = (): NavigationParams => {
        const urlParams = new URLSearchParams(window.location.search);
        const rawTab = urlParams.get('tab');
        const rawType = urlParams.get('type');
        const rawLang = urlParams.get('lang');
        const rawStartscene = urlParams.get('startscene');
        const rawId = urlParams.get('id');
        const rawCategory = urlParams.get('category');
        const rawSource = urlParams.get('source');
        const rawSingle = urlParams.get('single');

        const tab = rawTab && ALLOWED_TABS.has(rawTab) ? (rawTab as NavigationParams['tab']) : undefined;
        const type = rawType && ALLOWED_TYPES.has(rawType.toLowerCase()) ? rawType.toLowerCase() : undefined;
        const lang = rawLang && ALLOWED_LANGS.has(rawLang.toLowerCase()) ? rawLang.toLowerCase() : undefined;
        const id = sanitizeSafeParam(rawId);
        const startscene = sanitizeSafeParam(rawStartscene);
        const category = sanitizeSafeParam(rawCategory);
        const source = sanitizeSafeParam(rawSource);
        const single = sanitizeSafeParam(rawSingle);

        return {
            tab,
            id,
            type,
            lang,
            startscene,
            category,
            source,
            single
        };
    };

    const createNavigationUrl = (baseUrl: string, params: NavigationParams): string => {
        const urlParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value) urlParams.set(key, value);
        });

        return `${baseUrl}?${urlParams.toString()}`;
    };

    const navigateToGallery = (modelId: string, lang?: string) => {
        navigateToView({
            tab: 'nav_gallery',
            id: modelId,
            lang: lang,
        });
    };

    const navigateToAutoTour = (tourId: string, startScene?: string, lang?: string) => {
        navigateToView({
            tab: 'nav_autotour',
            id: tourId,
            startscene: startScene,
            lang: lang,
        });
    };

    const navigateToMaterial = (materialId: string, type: string, lang?: string) => {
        navigateToView({
            tab: 'nav_material',
            id: materialId,
            type: type,
            lang: lang,
        });
    };

    useEffect(() => {
        if (krpano) {
            const initializeScene = async () => {
                try {
                    // Sử dụng function nội bộ để parse URL
                    const urlParams = parseCurrentUrl();
                    const { startscene, tab, id, type, lang, category, source, single } = urlParams;

                    // 1. Xử lý startscene (ưu tiên cao nhất)
                    if (startscene) {
                        setCurrentScene(startscene);
                        if (typeof window !== 'undefined') {
                            (window as any).currentSceneName = startscene;
                        }

                        // Load scene trong Krpano
                        if (window.krpano && /^[a-zA-Z0-9_().-]+$/.test(startscene)) {
                            const escapedScene = "'" + String(startscene).replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'";
                            window.krpano.call(`loadscene(${escapedScene}, null, MERGE);`);
                        } else if (window.krpano) {
                            console.warn('Invalid startscene parameter format:', startscene);
                        }
                    }

                    // 2. Xử lý các tab khác nhau sử dụng function nội bộ
                    if (tab && id) {
                        // Delay một chút để đảm bảo components đã mount
                        setTimeout(() => {
                            switch (tab) {
                                case 'nav_autotour':
                                    if (window.handleOpenAutotour) {
                                        window.handleOpenAutotour(idPath, id);
                                    }
                                    break;

                                case 'nav_gallery':
                                    console.log(`🖼️ Opening Gallery detail with id: ${id}`);
                                    if (window.handleOpenGalleryDetail) {
                                        window.handleOpenGalleryDetail(idPath, id);
                                    }
                                    break;

                                case 'nav_material':
                                    const tryOpenMaterial = (attempts = 0) => {
                                        if (window.handleOpenMaterialDetail) {
                                            window.handleOpenMaterialDetail(
                                                id,
                                                String(type ?? ''),
                                                category ?? '',
                                                undefined,
                                                undefined,
                                                { source, single: source === 'recording' || single === '1' || !category }
                                            );
                                        } else if (attempts < 20) {
                                            setTimeout(() => tryOpenMaterial(attempts + 1), 250);
                                        }
                                    };
                                    tryOpenMaterial();
                                    break;
                                case 'nav_group_view':
                                    if (window.openGroupViewAtState) {
                                        window.openGroupViewAtState(idPath, id, type ?? undefined, lang);
                                    }
                                    break;
                                default:
                                    console.log(`⚠️ Unknown tab: ${tab}`);
                            }
                        }, 1000); // Delay 1 giây để components load xong
                    }

                    // 3. Nếu không có startscene, lấy scene đầu tiên từ Krpano
                    if (!startscene) {

                        const sceneInfo = await waitForFirstScene({ maxAttempts: 25, interval: 200 });
                        if (sceneInfo.sceneName && sceneInfo.sceneName !== currentScene) {
                            // Always sync to the actual scene loaded in krpano.
                            // Forcing home_view here can desync state and trigger wrong scene API calls.
                            setCurrentScene(sceneInfo.sceneName);
                        }

                        // setCurrentScene(projectValue.home_view);
                    }
                } catch (error) {
                    console.error('❌ Failed to initialize scene:', error);
                    // Fallback: thử lấy scene trực tiếp
                    const fallbackScene = getCurrentScene();
                    if (fallbackScene && fallbackScene !== currentScene) {
                        setCurrentScene(fallbackScene);
                    }
                }
            };

            initializeScene();
        }
    }, [krpano, idPath]); // Bỏ currentScene khỏi dependency để tránh loop

    // Track scene changes from krpano navigation
    // NOTE: currentScene is intentionally read via a ref so this effect only
    // mounts/unmounts when krpano or isReadyKrpano changes, not on every scene change.
    useEffect(() => {
        if (!krpano || !isReadyKrpano) return;

        const checkSceneChange = () => {
            try {
                if ((window as any).sg3dIntentionalNavigationPending) return;

                const newScene = krpano.get('xml.scene');
                if (newScene && krpanoSanitize(newScene) !== krpanoSanitize(currentSceneRef.current)) {
                    console.log('[KrpanoProvider] Scene changed:', currentSceneRef.current, '->', newScene);
                    if (typeof window !== 'undefined') {
                        (window as any).currentSceneName = String(newScene);
                    }
                    setCurrentScene(String(newScene));
                }
            } catch (error) {
                // Ignore errors when krpano is not ready
            }
        };

        checkSceneChange();

        // Listen to Krpano scene events
        (window as any).sg3dSyncScene = checkSceneChange;
        try {
            krpano.set('events[sg3d_scene_sync].name', 'sg3d_scene_sync');
            krpano.set('events[sg3d_scene_sync].keep', true);
            krpano.set('events[sg3d_scene_sync].onnewscene', 'js(window.sg3dSyncScene())');
            krpano.set('events[sg3d_scene_sync].onloadcomplete', 'js(window.sg3dSyncScene())');
        } catch {
            // ignore
        }

        // Check for scene changes periodically
        const intervalId = setInterval(checkSceneChange, 300);

        return () => {
            clearInterval(intervalId);
            try {
                krpano.set('events[sg3d_scene_sync].onnewscene', null);
                krpano.set('events[sg3d_scene_sync].onloadcomplete', null);
            } catch {
                // ignore
            }
        };
    }, [krpano, isReadyKrpano]); // ✅ No longer includes currentScene — interval stays stable

    // Sync currentScene to window.currentSceneName so it is accessible globally even before window.krpano is ready
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).currentSceneName = currentScene || null;
        }
    }, [currentScene]);

    // Automatically increment view count when the active scene changes (debounced 1s)
    useEffect(() => {
        const isAdminPage = typeof window !== 'undefined' && window.location.pathname.includes('/admin');
        if (isAdminPage) return;

        if (currentScene && idPath) {
            const timer = setTimeout(() => {
                sceneServices.incrementViewCount({
                    scene_name: currentScene,
                    id_path: idPath,
                }).catch((err) => console.error("Error incrementing view count:", err));
            }, 1000); // Debounce: only call if scene stays the same for 1s
            return () => clearTimeout(timer);
        }
    }, [currentScene, idPath]);

    // console.log('KrpanoProvider render', { krpano, isReadyKrpano, currentScene });

    return (
        <KrpanoContext.Provider
            value={{
                previousSceneRef,
                currentScene,
                setCurrentScene,
                isReadyKrpano, setIsReadyKrpano,

                krpano,
                setKrpano,
                krpanoCurrent,
                setKrpanoCurrent,
                sound,
                setSound,
                userInteracted,
                setUserInteracted,
                isTurnOnSound,
                setIsTurnOnSound,
                navigateToView,
                navigateToGallery,
                navigateToAutoTour,
                navigateToMaterial,
                parseCurrentUrl,
                createNavigationUrl,

            }}
        >
            {children}
        </KrpanoContext.Provider>
    );
};

export const useKrpano = () => {
    const context = useContext(KrpanoContext);
    if (context === undefined) {
        throw new Error('useKrpano must be used within a KrpanoProvider');
    }
    return context;
};
